/**
 * Languages offered only in the Quran translation dropdown (not the site language switcher).
 * Merged with codes returned from the Quran API so users can pick e.g. Swahili for meaning text.
 */

export const QURAN_EXTRA_TRANSLATION_LANGS: ReadonlyArray<{ code: string; label: string }> = [
  { code: 'am', label: 'አማርኛ (Amharic)' },
  { code: 'ha', label: 'Hausa' },
  { code: 'lg', label: 'Luganda' },
  { code: 'ln', label: 'Lingála' },
  { code: 'mg', label: 'Malagasy' },
  { code: 'ny', label: 'Chichewa' },
  { code: 'om', label: 'Afaan Oromoo' },
  { code: 'rw', label: 'Kinyarwanda' },
  { code: 'so', label: 'Soomaali' },
  { code: 'sw', label: 'Kiswahili' },
  { code: 'yo', label: 'Yorùbá' },
]

const EXTRA_CODES = QURAN_EXTRA_TRANSLATION_LANGS.map((x) => x.code)

/**
 * Quran.com / QUL API sometimes returns language names or non-ISO values as "iso_code".
 * Map those to short codes so the dropdown and getSurah() use the same keys as i18n where possible.
 */
const QURAN_LANG_ALIASES: Record<string, string> = {
  english: 'en',
  arabic: 'ar',
  french: 'fr',
  turkish: 'tr',
  urdu: 'ur',
  malay: 'ms',
  indonesian: 'id',
  bengali: 'bn',
  russian: 'ru',
  chinese: 'zh',
  persian: 'fa',
  spanish: 'es',
  german: 'de',
  italian: 'it',
  portuguese: 'pt',
  hindi: 'hi',
  dutch: 'nl',
  swedish: 'sv',
  polish: 'pl',
}

/** If API only sets language_name, match these hints to ISO (esp. African). */
export const QURAN_LANG_NAME_HINTS: Record<string, string[]> = {
  en: ['english'],
  ar: ['arabic'],
  fr: ['french'],
  tr: ['turkish'],
  ur: ['urdu'],
  ms: ['malay'],
  sw: ['swahili', 'kiswahili'],
  ha: ['hausa'],
  am: ['amharic', 'አማርኛ'],
  so: ['somali', 'soomaali'],
  yo: ['yoruba', 'yorùbá'],
  rw: ['kinyarwanda', 'runyaruanda'],
  lg: ['luganda', 'ganda'],
  ln: ['lingala', 'lingála'],
  mg: ['malagasy'],
  ny: ['chichewa', 'chewa', 'nyanja'],
  om: ['oromo', 'afaan oromoo', 'afaan'],
}

export function normalizeQuranLangCode(raw: string): string {
  const base = raw.split('-')[0].toLowerCase().trim()
  return QURAN_LANG_ALIASES[base] || base
}

/** Labels for select options: API codes + African extras. */
const BASE_LABELS: Record<string, string> = {
  ar: 'العربية',
  en: 'English',
  fr: 'Français',
  tr: 'Türkçe',
  ur: 'اردو',
  ms: 'Bahasa Melayu',
  id: 'Bahasa Indonesia',
  bn: 'বাংলা',
  ru: 'Русский',
  zh: '中文',
  fa: 'فارسی',
  es: 'Español',
  de: 'Deutsch',
  it: 'Italiano',
  pt: 'Português',
  hi: 'हिन्दी',
  nl: 'Nederlands',
  sv: 'Svenska',
  pl: 'Polski',
}

export function mergeQuranTranslationLanguageCodes(apiCodes: string[]): string[] {
  const normalized = apiCodes.map((c) => normalizeQuranLangCode(c))
  return [...new Set([...normalized, ...EXTRA_CODES])]
}

export function quranTranslationLanguageLabel(code: string): string {
  const base = normalizeQuranLangCode(code)
  const extra = QURAN_EXTRA_TRANSLATION_LANGS.find((x) => x.code === base)
  if (extra) return extra.label
  return BASE_LABELS[base] || code
}
